import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { useGetCompanyDepartments } from '@/services/queries/company-department';
import { usePatchEmployeeOrganizationLink } from '@/services/mutations/employee';

const NONE_SECTOR = '__none__';

const labelClass =
	'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';

const inputClass =
	'w-full rounded-xl border border-stone-200 bg-stone-50/50 px-4 py-3 text-sm font-bold text-stone-700 outline-none focus:border-stone-400 focus:bg-white';

const formatCPF = (value: string) => {
	const digits = value.replace(/\D/g, '');
	return digits
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const employeeLabel = (employee: Employees.Employee) => {
	const fromUser = employee.user
		? `${employee.user.first_name ?? ''} ${
				employee.user.last_name ?? ''
		  }`.trim()
		: '';
	if (fromUser) return fromUser;
	return `CPF ${formatCPF(employee.cpf)}`;
};

const resolveDepartmentName = (
	emp: Employees.Employee,
	depts: CompanyDepartments.CompanyDepartment[],
): string | null => {
	if (emp.department?.name) return emp.department.name;
	if (emp.department_id) {
		return depts.find((d) => d.id === emp.department_id)?.name ?? null;
	}
	return null;
};

const resolveSectorName = (
	emp: Employees.Employee,
	depts: CompanyDepartments.CompanyDepartment[],
): string | null => {
	if (emp.sector?.name) return emp.sector.name;
	if (!emp.sector_id) return null;
	const deptKey = emp.department_id ?? emp.department?.id;
	const dept = deptKey ? depts.find((d) => d.id === deptKey) : undefined;
	return (
		dept?.company_sector?.find((s) => s.id === emp.sector_id)?.name ?? null
	);
};

/** Valor do Select de setor a partir do employee + catálogo de departamentos. */
const deriveSectorChoiceForEmployee = (
	emp: Employees.Employee,
	depts: CompanyDepartments.CompanyDepartment[],
): string => {
	if (!emp.sector_id) return NONE_SECTOR;
	const dKey = emp.department_id ?? emp.department?.id;
	if (!dKey) return NONE_SECTOR;
	const dept = depts.find((d) => d.id === dKey);
	const ok = dept?.company_sector?.some((s) => s.id === emp.sector_id);
	return ok ? emp.sector_id : NONE_SECTOR;
};

type LinkCollaboratorOrgModalProps = {
	isOpen: boolean;
	onClose: () => void;
	companyId: string;
	employees: Employees.Employee[];
	/** Quando definido, o modal abre já para esse colaborador (sem trocar de colaborador no formulário). */
	prefilledEmployeeId?: string | null;
};

export const LinkCollaboratorOrgModal = ({
	isOpen,
	onClose,
	companyId,
	employees,
	prefilledEmployeeId = null,
}: LinkCollaboratorOrgModalProps) => {
	const [employeeId, setEmployeeId] = useState('');
	const [departmentId, setDepartmentId] = useState('');
	const [sectorId, setSectorId] = useState(NONE_SECTOR);

	const { mutateAsync: patchOrganizationLink, isPending } =
		usePatchEmployeeOrganizationLink(() => {
			onClose();
		});

	const { data: deptRes, isFetching: loadingDepts } =
		useGetCompanyDepartments(
			{ company_id: companyId, limit: 100 },
			{ enabled: isOpen && !!companyId },
		);

	const departments = deptRes?.data ?? [];

	const selectedDepartment = useMemo(
		() => departments.find((d) => d.id === departmentId),
		[departments, departmentId],
	);

	const sectors = selectedDepartment?.company_sector ?? [];

	const lockEmployeeSelect = !!prefilledEmployeeId;

	useEffect(() => {
		if (!isOpen) return;
		if (prefilledEmployeeId) return;
		setEmployeeId('');
		setDepartmentId('');
		setSectorId(NONE_SECTOR);
	}, [isOpen, prefilledEmployeeId]);

	useEffect(() => {
		if (!isOpen || !prefilledEmployeeId) return;
		setEmployeeId(prefilledEmployeeId);
		const emp = employees.find((e) => e.id === prefilledEmployeeId);
		if (!emp?.department_id) {
			setDepartmentId('');
			setSectorId(NONE_SECTOR);
		} else {
			setDepartmentId(emp.department_id);
			setSectorId(deriveSectorChoiceForEmployee(emp, departments));
		}
	}, [isOpen, prefilledEmployeeId, employees]);

	const handleEmployeeChange = (id: string) => {
		setEmployeeId(id);
		const emp = employees.find((e) => e.id === id);
		if (!emp?.department_id) {
			setDepartmentId('');
			setSectorId(NONE_SECTOR);
			return;
		}
		setDepartmentId(emp.department_id);
		setSectorId(deriveSectorChoiceForEmployee(emp, departments));
	};

	const handleDepartmentChange = (id: string) => {
		setDepartmentId(id);
		setSectorId(NONE_SECTOR);
	};

	/** Departamentos chegaram depois do colaborador: preenche setor salvo se ainda estiver em NONE. */
	useEffect(() => {
		if (!employeeId || loadingDepts) return;
		const emp = employees.find((e) => e.id === employeeId);
		if (!emp?.department_id || !emp.sector_id) return;
		setSectorId((prev) => {
			if (departmentId !== emp.department_id) return prev;
			if (prev !== NONE_SECTOR) return prev;
			return deriveSectorChoiceForEmployee(emp, departments);
		});
	}, [employeeId, departmentId, loadingDepts, departments, employees]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!employeeId || !departmentId || orgUnchanged) return;
		const body: Employees.PatchOrganizationLinkBody = {
			department_id: departmentId,
		};
		if (sectorId !== NONE_SECTOR) {
			body.sector_id = sectorId;
		}
		await patchOrganizationLink({
			id: employeeId,
			body,
		});
	};

	const selectedEmp = useMemo(
		() => employees.find((e) => e.id === employeeId),
		[employees, employeeId],
	);

	const orgUnchanged = useMemo(() => {
		if (!selectedEmp || !departmentId) return true;
		const targetSector = sectorId === NONE_SECTOR ? null : sectorId;
		const curDept = selectedEmp.department_id ?? null;
		const curSector = selectedEmp.sector_id ?? null;
		return (
			curDept === departmentId &&
			(curSector ?? null) === (targetSector ?? null)
		);
	}, [selectedEmp, departmentId, sectorId]);

	const canSubmit =
		!!employeeId &&
		!!departmentId &&
		employees.length > 0 &&
		!isPending &&
		!orgUnchanged;

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={handleSubmit}
					className="flex max-h-[90vh] flex-col overflow-hidden"
				>
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							Vincular Colaborador
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-stone-400 transition-colors hover:text-stone-600"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 overflow-y-auto px-8 py-6">
						{employees.length === 0 ? (
							<p className="text-sm font-medium text-stone-500">
								Não há colaboradores cadastrados nesta empresa.
								Cadastre um colaborador antes de vinculá-lo a
								departamento ou setor.
							</p>
						) : (
							<>
								<div className="space-y-2">
									<label
										className={labelClass}
										htmlFor={
											lockEmployeeSelect
												? undefined
												: 'link-collab-employee'
										}
									>
										Colaborador
									</label>
									{lockEmployeeSelect ? (
										<div
											id="link-collab-employee-locked"
											className={`${inputClass} cursor-default border-stone-100 bg-stone-100/80 text-stone-700`}
										>
											{(() => {
												const picked = employees.find(
													(x) =>
														x.id ===
														prefilledEmployeeId,
												);
												return picked
													? employeeLabel(picked)
													: 'Colaborador não encontrado';
											})()}
										</div>
									) : (
										<Select
											value={employeeId}
											onValueChange={handleEmployeeChange}
										>
											<SelectTrigger
												id="link-collab-employee"
												className={inputClass}
											>
												<SelectValue>
													{(() => {
														const picked =
															employees.find(
																(x) =>
																	x.id ===
																	employeeId,
															);
														return picked
															? employeeLabel(
																	picked,
															  )
															: 'Selecione o colaborador';
													})()}
												</SelectValue>
											</SelectTrigger>
											<SelectContent dropdownWidth="max-content">
												{employees.map((emp) => (
													<SelectItem
														key={emp.id}
														value={emp.id}
														noTruncate
													>
														{employeeLabel(emp)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
									<div className="mt-3 rounded-xl border border-stone-100 bg-stone-50/60 px-4 py-3">
										{!employeeId ? (
											<p className="text-sm font-medium text-stone-500">
												Selecione um colaborador acima
												para ver e-mail, CPF e vínculo
												atual com departamento e setor.
											</p>
										) : (
											(() => {
												const emp = employees.find(
													(x) => x.id === employeeId,
												);
												if (!emp) return null;
												const deptDisplay =
													resolveDepartmentName(
														emp,
														departments,
													) ?? '—';
												const sectorDisplay =
													resolveSectorName(
														emp,
														departments,
													) ?? '—';
												return (
													<div className="space-y-2 text-sm font-medium text-stone-600">
														<p>
															<span className="text-stone-400">
																E-mail:{' '}
															</span>
															{emp.user?.email?.trim() ||
																'—'}
														</p>
														<p>
															<span className="text-stone-400">
																CPF:{' '}
															</span>
															{formatCPF(emp.cpf)}
														</p>
														<p>
															<span className="text-stone-400">
																Status:{' '}
															</span>
															{emp.active
																? 'Ativo'
																: 'Inativo'}
														</p>
														<p>
															<span className="text-stone-400">
																Departamento:{' '}
															</span>
															{deptDisplay}
														</p>
														<p>
															<span className="text-stone-400">
																Setor:{' '}
															</span>
															{sectorDisplay}
														</p>
													</div>
												);
											})()
										)}
									</div>
								</div>

								<div className="space-y-2">
									<label
										className={labelClass}
										htmlFor="link-collab-dept"
									>
										Departamento
									</label>
									{!loadingDepts &&
									departments.length === 0 ? (
										<p className="text-sm font-medium text-stone-500">
											Nenhum departamento vinculado a esta
											empresa. Vincule um departamento na
											aba Departamentos.
										</p>
									) : null}
									<Select
										value={departmentId}
										onValueChange={handleDepartmentChange}
										disabled={
											loadingDepts ||
											departments.length === 0
										}
									>
										<SelectTrigger
											id="link-collab-dept"
											className={inputClass}
										>
											<SelectValue>
												{loadingDepts
													? 'Carregando...'
													: departmentId
													  ? departments.find(
																(d) =>
																	d.id ===
																	departmentId,
													    )?.name ??
													    selectedEmp?.department
																?.name ??
													    ''
													  : 'Selecione o departamento'}
											</SelectValue>
										</SelectTrigger>
										<SelectContent dropdownWidth="max-content">
											{departments.map((d) => (
												<SelectItem
													key={d.id}
													value={d.id}
													noTruncate
												>
													{d.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<label
										className={labelClass}
										htmlFor="link-collab-sector"
									>
										Setor (opcional)
									</label>
									<Select
										value={sectorId}
										onValueChange={setSectorId}
										disabled={!departmentId}
									>
										<SelectTrigger
											id="link-collab-sector"
											className={inputClass}
										>
											<SelectValue>
												{!departmentId
													? 'Selecione o departamento primeiro'
													: sectorId === NONE_SECTOR
													  ? 'Sem setor específico'
													  : sectors.find(
																(s) =>
																	s.id ===
																	sectorId,
													    )?.name ??
													    'Sem setor específico'}
											</SelectValue>
										</SelectTrigger>
										<SelectContent dropdownWidth="max-content">
											<SelectItem
												value={NONE_SECTOR}
												noTruncate
											>
												Sem setor específico
											</SelectItem>
											{sectors.map((s) => (
												<SelectItem
													key={s.id}
													value={s.id}
													noTruncate
												>
													{s.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</>
						)}
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 px-8 py-6">
						<button
							type="button"
							onClick={onClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={isPending ? 'Vinculando...' : 'Vincular'}
							disabled={!canSubmit}
							className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-stone-300"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
